/**
 * BryntumImporter — ES module port of the Importer class from the old app's
 * show-bryntum.js (lines 1986-2745).
 *
 * Imports XER / MPXJ-parsed schedule data into a Bryntum Gantt project:
 *   calendars, tasks, resources, assignments, dependencies, columns.
 *
 * Usage:
 *   import BryntumImporter from "../lib/bryntum_importer"
 *   const importer = new BryntumImporter({ gantt, syncUrl, defaultColumns })
 *   const project = await importer.importData(data, scheduleId)
 */
export default class BryntumImporter {

  /**
   * @param {Object}  config
   * @param {Object}  config.gantt          - Bryntum Gantt instance
   * @param {string}  config.syncUrl        - URL for data sync (e.g. "/en/projects/123/schedules/456/tasks/sync")
   * @param {Array}   [config.defaultColumns] - Fallback column definitions when import data has none
   */
  constructor({ gantt, syncUrl, defaultColumns, csrfToken, preserveDates = false }) {
    this.gantt          = gantt;
    this.syncUrl        = syncUrl;
    this.defaultColumns = defaultColumns;
    this.csrfToken      = csrfToken;
    // Progress/track imports keep the file's own dates (see processTask).
    this.preserveDates  = preserveDates;
    // Real clock-hours per MS Project elapsed-duration unit (ed=elapsed day, etc.).
    this.ELAPSED_HOURS  = { ed: 24, ew: 168, eh: 1, em: 1 / 60, emo: 720, ey: 8760 };
  }

  // Lazily create one shared always-working calendar for elapsed-duration tasks.
  elapsedCalendar() {
    if (!this._elapsedCal) {
      this._elapsedCal = new this.calendarManager.modelClass({
        name                     : 'Elapsed (24/7)',
        unspecifiedTimeIsWorking : true,
        intervals                : []
      });
      this.calendarManager.add(this._elapsedCal);
    }
    return this._elapsedCal;
  }

  // ---------------------------------------------------------------------------
  // Main entry point
  // ---------------------------------------------------------------------------

  async importData(data, scheduleId = null) {
    const me = this;

    const project = new me.gantt.projectModelClass({
      // Register the MS Project "Quantity" custom field on the task model so it is
      // retained and synced (the value is merged into each task server-side).
      taskStore : { fields : [{ name : 'quantity', type : 'string' }] },
      // Disable autoSetConstraints during import to prevent Bryntum from injecting
      // extra constraints that don't exist in the P6 source data. P6 constraints are
      // imported explicitly from the XER file via MPXJ.
      autoSetConstraints   : false,
      silenceInitialCommit : false,
      autoSync             : false,
      // Use the task's working-time calendar (Mon-Fri 8-5) — not Bryntum's 24/7
      // default — when computing dates and durations for manually scheduled tasks.
      // Without these, an imported task like "May 26 → Jun 26, 24 days" gets its
      // end date rewound to Jun 3 (24d × 8h × 24/7) or, after a manual end-date
      // fix, its duration ballooned to 92.
      skipNonWorkingTimeWhenSchedulingManually           : true,
      skipNonWorkingTimeInDurationWhenSchedulingManually : true,
      transport            : {
        sync : {
          url           : me.syncUrl,
          requestConfig : {
            headers : { 'X-CSRF-Token' : me.csrfToken || '' }
          }
        }
      }
    });

    // Auto-resolve scheduling conflicts during XER import.
    // P6 allows tasks to violate "Finish No Later Than" / "Start No Later Than" constraints
    // by assigning negative total float. Bryntum instead raises a conflict popup.
    // This listener silently resolves conflicts by removing the constraint and keeping
    // the dependency, which matches P6's behavior (the task just gets negative float).
    project.on('schedulingconflict', ({ continueWithResolutionResult, conflict }) => {
      console.warn('Auto-resolving scheduling conflict during import:', conflict?.description);

      // Find the resolution that removes the constraint (keeps the dependency intact)
      const resolutions = conflict?.getResolutions?.() || [];
      let constraintRemovalIndex = -1;

      for (let i = 0; i < resolutions.length; i++) {
        const resolution = resolutions[i];
        const desc = resolution?.getDescription?.() || '';
        // Look for the resolution that removes the constraint
        if (desc.toLowerCase().includes('remove') && desc.toLowerCase().includes('constraint')) {
          constraintRemovalIndex = i;
          break;
        }
        // Also match deactivating a dependency as fallback for dependency-vs-dependency conflicts
        if (desc.toLowerCase().includes('deactivate') && desc.toLowerCase().includes('dependency')) {
          if (constraintRemovalIndex === -1) constraintRemovalIndex = i;
        }
      }

      // If we found a constraint removal resolution, use it; otherwise pick the last option
      // (Bryntum typically puts the "remove constraint" option last)
      const resolutionIndex = constraintRemovalIndex !== -1
        ? constraintRemovalIndex
        : Math.max(resolutions.length - 1, 0);

      continueWithResolutionResult(resolutionIndex);
    });

    // Auto-resolve empty calendar errors during XER import.
    // MPXJ may produce a "General" (or other parent) calendar without working time intervals.
    // When Bryntum's engine encounters a task assigned to such a calendar, it raises an
    // "emptyCalendar" event. We auto-resolve by applying an 8-hour work day.
    project.on('emptycalendar', ({ schedulingIssue }) => {
      const calendarName = schedulingIssue?.getCalendar?.()?.name || 'unknown';
      console.warn(`Auto-resolving empty calendar "${calendarName}" during import — applying 8-hour work day`);

      const resolutions = schedulingIssue?.getResolutions?.() || [];
      // Resolution[0] = 24-hour calendar, Resolution[1] = 8-hour calendar (08:00-12:00, 13:00-17:00)
      const use8hrIndex = resolutions.length > 1 ? 1 : 0;
      resolutions[use8hrIndex]?.resolve?.();
    });

    me.project         = project;
    me.calendarManager = project.calendarManagerStore;
    me.taskStore       = project.taskStore;
    me.assignmentStore = project.assignmentStore;
    me.resourceStore   = project.resourceStore;
    me.dependencyStore = project.dependencyStore;

    Object.assign(me, {
      calendarMap : {},
      resourceMap : {},
      taskMap     : {}
    });

    // Store project settings early so they're available during task processing
    // hoursPerDay comes from XER's day_hr_cnt field via the Java project reader
    me.importedProjectSettings = data.project || {};

    me.importCalendars(data);

    const tasks = me.getTaskTree(Array.isArray(data.tasks) ? data.tasks : [data.tasks]);

    me.importResources(data);
    me.importAssignments(data);

    // A root with nothing under it is a leaf whose `children` isn't a list.
    const topTasks = tasks[0]?.children;
    me.taskStore.rootNode.appendChild(Array.isArray(topTasks) ? topTasks : []);

    me.importDependencies(data);

    me.importProject(data);

    // Suppress the Gantt's built-in scheduling issue resolution popup during import.
    // Detach BEFORE assigning the project to prevent a race condition where
    // the gantt binds popup listeners synchronously during assignment.
    me.gantt.detachListeners('schedulingIssueResolution');

    // Assign the new project to the gantt before launching commitAsync()
    // Scheduling conflicts (if any) will be auto-resolved by the listeners above
    me.gantt.project = project;

    // Detach again — gantt rebinds popup listeners when a new project is assigned
    me.gantt.detachListeners('schedulingIssueResolution');

    await me.project.commitAsync();
    await me.project.propagate(); // Ensure scheduling logic is applied

    // Remove the import-time conflict/calendar resolvers so they don't interfere
    // with user-initiated changes (where the user should see the popups)
    project.un('schedulingconflict');
    project.un('emptycalendar');

    // Re-bind the Gantt's scheduling issue resolution popup listeners for normal
    // user interaction (so users see conflict/calendar popups when editing tasks)
    me.gantt.bindSchedulingIssueResolutionToProject(project);

    // Re-enable autoSetConstraints for normal user interaction after import
    project.autoSetConstraints = true;

    await me.project.sync(); // Wait for data to be saved before returning

    me.gantt.render(); // Ensure the Gantt chart is refreshed

    return project;
  }

  // ---------------------------------------------------------------------------
  // Resources
  // ---------------------------------------------------------------------------

  importResources(data) {
    this.resourceStore.add(data.resources.map(this.processResource, this));
  }

  processResource(data) {
    const { id } = data;

    delete data.id;

    data.calendar = this.calendarMap[data.calendar];

    const resource = new this.resourceStore.modelClass(data);

    this.resourceMap[id] = resource;

    return resource;
  }

  // ---------------------------------------------------------------------------
  // Dependencies
  // ---------------------------------------------------------------------------

  importDependencies(data) {
    const deps = data.dependencies.map(this.processDependency, this).filter(Boolean);
    if (deps.length > 0) this.dependencyStore.add(deps);
  }

  processDependency(data) {
    const me = this;
    const { fromEvent, toEvent } = data;

    if (!me.taskMap[fromEvent] || !me.taskMap[toEvent]) {
      console.warn(`Skipping dependency: missing task reference (from=${fromEvent}, to=${toEvent})`);
      return null;
    }

    delete data.id;

    const dep = new me.dependencyStore.modelClass(data);

    dep.fromEvent = me.taskMap[fromEvent].id;
    dep.toEvent   = me.taskMap[toEvent].id;

    return dep;
  }

  // ---------------------------------------------------------------------------
  // Assignments
  // ---------------------------------------------------------------------------

  importAssignments(data) {
    this.assignmentStore.add(data.assignments.map(this.processAssignment, this));
  }

  processAssignment(data) {
    const me = this;

    delete data.id;

    return new me.assignmentStore.modelClass({
      units    : data.units,
      event    : me.taskMap[data.event],
      resource : me.resourceMap[data.resource]
    });
  }

  // ---------------------------------------------------------------------------
  // Tasks
  // ---------------------------------------------------------------------------

  getTaskTree(tasks) {
    return tasks.map(this.processTask, this);
  }

  processTask(data) {
    const me = this;
    const { id, children } = data;

    delete data.children;
    delete data.id;

    // The project reader writes MS Project's default "As Soon As Possible"
    // (and ALAP) as a blank constraint type. Bryntum's Constraint type picker
    // can't match "" to its list, so the task pop-up wouldn't save ("Value
    // must be selected from the list" — Doug Stapley, Harman Huffman
    // 2026-11-02). No constraint is null.
    if (data.constraintType === '') data.constraintType = null;

    // Convert hour durations to days using hoursPerDay from project settings.
    // This handles XER files where durations are stored in hours (target_drtn_hr_cnt)
    // and accounts for implicit lunch breaks (e.g., 8:00-17:00 span with 8 working hours).
    if (data.duration != null && (data.durationUnit === 'h' || data.durationUnit === 'hour' || data.durationUnit === 'hours')) {
      // Get hoursPerDay from imported project settings (derived from XER's day_hr_cnt field)
      // Falls back to 8 hours/day which is the standard for most P6 calendars
      const hoursPerDay = me.importedProjectSettings?.hoursPerDay || 8;
      data.duration     = data.duration / hoursPerDay;
      data.durationUnit = 'd';
    }

    // MS Project elapsed durations (ed/ew/eh/em/emo/ey) run continuously —
    // through nights, weekends, and holidays — e.g. "Concrete Cure Time".
    // Bryntum has no native elapsed unit, so convert to real clock hours and
    // (below) schedule the task on a 24/7 calendar. Without this, an elapsed
    // "14d" cure is treated as 14 *working* days (~19 calendar days), which
    // pushes every successor days late and inflates summary durations.
    let isElapsed = false;
    if (data.duration != null && me.ELAPSED_HOURS[data.durationUnit] != null) {
      data.duration     = data.duration * me.ELAPSED_HOURS[data.durationUnit];
      data.durationUnit = 'h';
      isElapsed         = true;
    }

    // Ensure dates are properly parsed as Date objects
    if (data.startDate && typeof data.startDate === 'string') {
      data.startDate = new Date(data.startDate);
    }

    if (data.endDate && typeof data.endDate === 'string') {
      data.endDate = new Date(data.endDate);
    }

    // Assign the calendar to the task
    data.calendar = me.calendarMap[data.calendar] || me.project.calendar;

    // Elapsed-duration tasks ignore working time — put them on a shared 24/7
    // calendar so their (hour) duration spans real clock time.
    if (isElapsed) {
      data.calendar = me.elapsedCalendar();
    }

    // P6 XER Import: Force constrained milestones to be manually scheduled.
    // In P6, milestones with constraints like "Finish No Later Than" (CS_MEOB) or
    // "Must Finish On" (CS_MEO) have their dates calculated by P6's CPM engine.
    // If we let Bryntum recalculate these, dependencies may push them past their
    // constraint dates, causing scheduling conflicts that P6 handles via negative float.
    // By marking them as manually scheduled, we preserve P6's exact calculated dates
    // and prevent Bryntum from overriding them during propagation.
    const constrainedMilestoneTypes = [
      'finishnolaterthan', 'startnolaterthan',
      'mustfinishon', 'muststarton'
    ];
    if (data.constraintType && constrainedMilestoneTypes.includes(data.constraintType)) {
      if (data.milestone || data.duration === 0) {
        data.manuallyScheduled = true;
      }
    }

    // Progress ("track") import: the file already says where the job is, so
    // every dated leaf is pinned to its own dates (Larry 2026-08-04). Left to
    // the scheduling engine, a tracked file — whose constraints get dropped
    // by the schedulingconflict handler above — is re-flowed ASAP from the
    // project start, which is how every activity ended up starting March 2.
    // Summary rows still roll up from their children.
    if (me.preserveDates && !children?.length && data.startDate && data.endDate) {
      data.manuallyScheduled = true;
    }

    // Special handling for manually scheduled tasks
    if (data.manuallyScheduled === true) {
      // For manually scheduled tasks with 1 day duration, ensure the end date is set correctly
      if (data.duration === 1 && (data.durationUnit === 'd' || data.durationUnit === 'day' || data.durationUnit === 'days')) {
        const startDate      = new Date(data.startDate);
        const currentEndDate = new Date(data.endDate);

        // If the end date is on the same day but wrong time, fix it
        if (startDate.getDate() === currentEndDate.getDate() &&
            startDate.getMonth() === currentEndDate.getMonth() &&
            startDate.getFullYear() === currentEndDate.getFullYear()) {

          // Set end date to the end of the working day (5:00 PM) on the same day
          const correctedEndDate = new Date(startDate);
          correctedEndDate.setHours(17, 0, 0, 0);
          data.endDate = correctedEndDate;
        }
      }
    }
    // Regular handling for auto-scheduled tasks
    else {
      if (data.duration === 1 && (!data.durationUnit || data.durationUnit === 'd' || data.durationUnit === 'day' || data.durationUnit === 'days')) {
        data.durationUnit = 'd';
      }
    }

    const t = new me.taskStore.modelClass(data);

    if (children) {
      t.appendChild(me.getTaskTree(children));
    }

    // Keyed by the file's id, which never becomes the record's id: setting
    // `t._id` (as Bryntum's import demo does) left every row "new" after the
    // first save, so the baseline save sent them all again — each activity
    // showed up twice (Keane K3-18, 2026-10-07).
    me.taskMap[id] = t;

    return t;
  }

  // ---------------------------------------------------------------------------
  // Calendars
  // ---------------------------------------------------------------------------

  processCalendarChildren(children) {
    return children.map(this.processCalendar, this);
  }

  processCalendar(data) {
    const me = this;
    const { id, children } = data;
    const intervals = data.intervals || [];

    delete data.children;
    delete data.id;

    // Check if the imported data explicitly sets unspecifiedTimeIsWorking to true (indicating 24/7)
    const is247 = data.unspecifiedTimeIsWorking === true;

    // Check if we have any working time intervals defined with specific times
    const hasSpecificWorkingIntervals = intervals.some(interval =>
      interval.isWorking &&
      interval.recurrentStartDate && interval.recurrentStartDate.includes(' at ') &&
      interval.recurrentEndDate && interval.recurrentEndDate.includes(' at ')
    );

    // If it's NOT a 24/7 calendar AND it lacks specific working intervals, apply the default 8-5 schedule.
    if (!is247 && !hasSpecificWorkingIntervals) {
      intervals.push(...me.getDefaultWorkingHoursIntervals());
      data.unspecifiedTimeIsWorking = false;
    }

    const t = new me.calendarManager.modelClass(Object.assign(data, { intervals }));

    if (children) {
      t.appendChild(me.processCalendarChildren(children));
    }

    me.calendarMap[id] = t; // file id → record, as for tasks (K3-18)

    return t;
  }

  getDefaultWorkingHoursIntervals() {
    // Define non-working days (weekends)
    const nonWorkingDays = [
      {
        recurrentStartDate : 'on Sat at 0:00',
        recurrentEndDate   : 'on Mon at 0:00',
        isWorking          : false
      }
    ];

    // Define working hours for weekdays
    const workingHours = [
      // Morning shift
      {
        recurrentStartDate : 'on Mon,Tue,Wed,Thu,Fri at 8:00',
        recurrentEndDate   : 'on Mon,Tue,Wed,Thu,Fri at 12:00',
        isWorking          : true
      },
      // Lunch break
      {
        recurrentStartDate : 'on Mon,Tue,Wed,Thu,Fri at 12:00',
        recurrentEndDate   : 'on Mon,Tue,Wed,Thu,Fri at 13:00',
        isWorking          : false
      },
      // Afternoon shift
      {
        recurrentStartDate : 'on Mon,Tue,Wed,Thu,Fri at 13:00',
        recurrentEndDate   : 'on Mon,Tue,Wed,Thu,Fri at 17:00',
        isWorking          : true
      },
      // Non-working time after hours
      {
        recurrentStartDate : 'on Mon,Tue,Wed,Thu,Fri at 17:00',
        recurrentEndDate   : 'on Mon,Tue,Wed,Thu,Fri at 8:00',
        isWorking          : false
      }
    ];

    return [...nonWorkingDays, ...workingHours];
  }

  importCalendars(data) {
    this.calendarManager.add(this.processCalendarChildren(data.calendars.children));
  }

  // ---------------------------------------------------------------------------
  // Columns
  // ---------------------------------------------------------------------------

  importColumns(data) {
    let columns = data.columns.map(this.processColumn, this).filter(column => column);

    const columnStore = this.gantt.subGrids.locked.columns;

    // If no columns extracted, apply default set (if configured)
    if (!columns.length && this.defaultColumns) {
      columns = this.defaultColumns;
    }

    if (columns.length) {
      columnStore.removeAll(true);
      columnStore.add(columns);
    }
  }

  processColumn(data) {
    const columnClass = this.gantt.columns.constructor.getColumnClass(data.type);

    // Ignore unknown columns (or columns whose classes are not loaded)
    if (columnClass) {
      return Object.assign({ region : 'locked' }, data);
    }
  }

  // ---------------------------------------------------------------------------
  // Project
  // ---------------------------------------------------------------------------

  importProject(data) {
    // If project calendar is defined, use it
    if ('calendar' in data.project) {
      data.project.calendar = this.calendarMap[data.project.calendar];
    } else {
      // If no calendar is defined, find the default calendar or create one
      const defaultCalendar = this.calendarManager.find(cal => cal.name === 'General') ||
                              this.calendarManager.first;

      if (defaultCalendar) {
        data.project.calendar = defaultCalendar;
      } else {
        // Create a new calendar with default working hours
        const newCalendar = new this.calendarManager.modelClass({
          name                    : 'General',
          intervals               : this.getDefaultWorkingHoursIntervals(),
          unspecifiedTimeIsWorking : false
        });

        this.calendarManager.add(newCalendar);
        data.project.calendar = newCalendar;
      }
    }

    // Apply data to project
    Object.assign(this.project, data.project);

    // Use imported project settings if available, otherwise fall back to defaults
    // These values come from the XER file's day_hr_cnt, week_hr_cnt fields
    this.project.hoursPerDay  = data.project?.hoursPerDay  || this.importedProjectSettings?.hoursPerDay  || 8;
    this.project.daysPerWeek  = data.project?.daysPerWeek  || this.importedProjectSettings?.daysPerWeek  || 5;
    this.project.daysPerMonth = data.project?.daysPerMonth || this.importedProjectSettings?.daysPerMonth || 20;
  }
}
